import { computed } from '@vue/composition-api';
import { useStore } from '@nuxtjs/composition-api';
import { endOfISOWeek, format, isSameDay, parse, startOfISOWeek } from 'date-fns';
import { useUser } from '~/data/account/use-user';
import { useFeatureFlag } from '~/data/account/use-feature-flag';
import { DateFormat } from '~/helpers/date';
import { State } from '~/data/types/store';
import { CalendarDay } from '~/data/types';

export type TrainingSummaryStat = {
  type: string,
  distance: { value: number, units: string } | null,
  duration_moving: { value: number[], units: string[] },
};

export type TrainingSummary = {
  completed_workouts_percentage: number,
  completed_workouts: number,
  stats: TrainingSummaryStat[],
  total_workouts: number,
  workout_types: string[],
}

export const useTrainingSummary = () => {
  const store = useStore<State>();
  const { currentDate } = useUser();
  const { hasAccessToWeekEndStats } = useFeatureFlag();
  const isLoadingSummaries = computed(() => store.state.trainingSummary.isLoadingSummaries.length);

  const isLoadingSummary = (fromDate: string, toDate: string): boolean => {
    if (!hasAccessToWeekEndStats.value) {
      return false;
    }

    const key = `${fromDate}-${toDate}`;
    return isLoadingSummaryForKey(key);
  };

  const isLoadingSummaryForCurrentWeek = (): boolean => {
    if (!hasAccessToWeekEndStats.value) {
      return false;
    }

    const fromDate = format(startOfISOWeek(currentDate.value), DateFormat.Date);
    const toDate = format(currentDate.value, DateFormat.Date);
    const key = `${fromDate}-${toDate}`;
    return isLoadingSummaryForKey(key);
  };

  const isLoadingSummaryForKey = (key: string): boolean => {
    return store.state.trainingSummary.isLoadingSummaries.includes(key);
  };

  const getSummary = async(fromDate: string, toDate: string, forceUpdate: boolean = false): Promise<TrainingSummary | null> => {
    if (!hasAccessToWeekEndStats.value) {
      return null;
    }

    const key = `${fromDate}-${toDate}`;

    if (isLoadingSummaryForKey(key)) {
      return null;
    }

    if (store.state.trainingSummary.summaries[key] && !forceUpdate) {
      return store.state.trainingSummary.summaries[key];
    }

    await store.dispatch('trainingSummary/GET_TRAINING_SUMMARY', { fromDate, toDate });
    return store.state.trainingSummary.summaries[key] ?? null;
  };

  const getSummaryForCurrentWeek = async(): Promise<TrainingSummary | null> => {
    if (!hasAccessToWeekEndStats.value) {
      return null;
    }

    const fromDate = format(startOfISOWeek(currentDate.value), DateFormat.Date);
    const toDate = format(currentDate.value, DateFormat.Date);
    const key = `${fromDate}-${toDate}`;

    if (isLoadingSummaryForKey(key)) {
      return null;
    }

    if (store.state.trainingSummary.summaries[key]) {
      return store.state.trainingSummary.summaries[key];
    }

    await store.dispatch('trainingSummary/GET_TRAINING_SUMMARY', { fromDate, toDate });
    return store.state.trainingSummary.summaries[key] ?? null;
  };

  const getSummaryActivityTypesFromCalendar = (days: CalendarDay[]): string[] => {
    if (!hasAccessToWeekEndStats.value) {
      return [];
    }

    let hasSwim = false;
    let hasBike = false;
    let hasRun = false;
    let hasOther = false;

    days.forEach((day) => {
      day.activities.forEach((activity) => {
        const activityType = (activity.type ?? '').toLowerCase();
        if (activityType.includes('swim')) {
          hasSwim = true;
        } else if (activityType.includes('bike')) {
          hasBike = true;
        } else if (activityType.includes('run')) {
          hasRun = true;
        } else {
          hasOther = true;
        }
      });
    });

    const activities: string[] = [];

    if (hasSwim) {
      activities.push('swim');
    }
    if (hasBike) {
      activities.push('bike');
    }
    if (hasRun) {
      activities.push('run');
    }
    if (hasOther) {
      activities.push('other');
    }

    return activities;
  };

  const updateWeekSummary = (date: string) => {
    if (!hasAccessToWeekEndStats.value) {
      return;
    }

    const startOfWeek = startOfISOWeek(parse(date, DateFormat.Date, currentDate.value));
    let endOfWeek: Date;

    // If this is the current week, then make the end of week today
    if (isSameDay(startOfISOWeek(currentDate.value), startOfWeek)) {
      endOfWeek = currentDate.value;
    } else {
      endOfWeek = endOfISOWeek(startOfWeek);
    }

    getSummary(format(startOfWeek, DateFormat.Date), format(endOfWeek, DateFormat.Date), true);
  };

  return {
    getSummary,
    getSummaryActivityTypesFromCalendar,
    getSummaryForCurrentWeek,
    isLoadingSummaries,
    isLoadingSummary,
    isLoadingSummaryForCurrentWeek,
    updateWeekSummary,
  };
};
